// Ожидание пробуждения принтера (замер на Xprinter V3BT, 2026-09-26).
//
// Принтер засыпает после 3–5 минут простоя и не отвечает на подключение Bluetooth,
// пока не проснётся: 37–41 с непрерывных попыток после 5–7 минут простоя, 76 с —
// после 22 минут. Печать с тремя попытками по ~4 с (`sendWithRetry`, 18,5 с)
// сдавалась раньше — на кухне это «Принтер не отвечает» на первой этикетке после
// перерыва, и повар жал «Печать» снова, пока принтер не просыпался.
//
// Теперь подключение пробует без перерыва до `WAKE_UP_CAP_MS`, а экран честно
// говорит, что ждёт принтер (`subscribePrinterWaking`). Тот же путь выручает, когда
// принтер держит другое устройство: печать дождётся, пока слот освободится.
//
// Ждать стоит не всякого отказа. Просыпаясь, принтер сначала молчит (попытка висит
// до своего таймаута), а прямо перед удачным подключением один раз ОТКАЗЫВАЕТ
// (Windows: 10022) — так было на всех трёх замерах. Отказ подряд три раза — уже не
// сон: принтеру, например, нужно сопряжение, и экран настройки должен узнать об
// этом за секунды, а не через две минуты.

import { sleep } from './retryPolicy';

/**
 * Потолок ожидания — с запасом к замеренным 76 с. Потолок обязателен (урок B-023):
 * выключенный принтер не проснётся никогда, и печать обязана это сказать.
 */
export const WAKE_UP_CAP_MS = 120_000;

/** Столько отказов подряд — и это уже не пробуждение, а отказ. */
export const MAX_REFUSALS_IN_A_ROW = 3;

/**
 * Попытки не чаще, чем раз в 2 с: мгновенный отказ (помощник оболочки ещё не
 * поднялся, адаптер занят поиском) иначе молотил бы стек Bluetooth впустую. Спящий
 * принтер это не задерживает — его попытка сама длится 4–30 с.
 */
const WAKE_UP_TRY_EVERY_MS = 2000;

/**
 * Чем кончилась неудачная попытка:
 * - `permanent` — ожидание не исправит (нет разрешения, выключен Bluetooth);
 * - `silent` — принтер не ответил вовсе: спит, занят другим устройством или далеко;
 * - `refused` — ответил отказом: бывает раз при пробуждении, но не три раза подряд.
 */
export type ConnectFailureKind = 'permanent' | 'silent' | 'refused';

let waitingCount = 0;
const listeners = new Set<() => void>();

function changeWaiting(delta: 1 | -1): void {
  waitingCount += delta;
  for (const listener of listeners) listener();
}

/** Ждёт ли сейчас приложение, пока принтер проснётся или освободится. */
export function isPrinterWaking(): boolean {
  return waitingCount > 0;
}

/** Подписка для экрана (`useSyncExternalStore`). */
export function subscribePrinterWaking(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export type ConnectAwakeOptions<T> = {
  /** Одна попытка подключения; бросает при отказе. */
  readonly tryConnect: () => Promise<T>;
  /** Прибрать за неудачной попыткой: закрыть сокет, отменить начатое подключение. */
  readonly afterFailure: () => Promise<unknown>;
  readonly classify: (error: unknown) => ConnectFailureKind;
  readonly capMs?: number;
  readonly now?: () => number;
};

/**
 * Подключиться, дождавшись пробуждения принтера. Первая неудача включает признак
 * ожидания для экрана; дальше — попытки подряд до потолка. Бросает ошибку последней
 * попытки: постоянную — сразу, отказ — на третьем подряд, молчание — по потолку.
 */
export async function connectAwake<T>(options: ConnectAwakeOptions<T>): Promise<T> {
  const { tryConnect, afterFailure, classify } = options;
  const capMs = options.capMs ?? WAKE_UP_CAP_MS;
  const now = options.now ?? Date.now;
  const started = now();
  let announced = false;
  let refusals = 0;
  try {
    for (;;) {
      const tried = now();
      try {
        return await tryConnect();
      } catch (error) {
        await afterFailure().catch(() => undefined);
        const kind = classify(error);
        refusals = kind === 'refused' ? refusals + 1 : 0;
        if (kind === 'permanent' || refusals >= MAX_REFUSALS_IN_A_ROW || now() - started >= capMs) {
          throw error;
        }
        if (!announced) {
          announced = true;
          changeWaiting(1);
        }
        await sleep(Math.max(0, WAKE_UP_TRY_EVERY_MS - (now() - tried)));
      }
    }
  } finally {
    if (announced) {
      changeWaiting(-1);
    }
  }
}
